import { useEffect, useRef, useState } from "react";
import { Drawer, DrawerRef } from "../../../../components/drawers/Drawer";
import Checkbox from "../../../../components/form/Checkbox";
import { JobsChartSegments } from "../../../../core/Enums";
import { JobsReportRequest, REPORT_DATE_TYPES, effectiveDateType } from "../models/JobsReportRequest";
import Select from "../../../../components/form/Select";
import { DataColumnsVisibility, JobReportDataRow, JobReportTechPaymentRow, JobsColumnsVisibility, TechPayColumnsVisibility } from "../models/JobReportModel";
import Job from "../../../crm/jobs/models/Job";


// Every column toggle of the Jobs Report lives here. They used to be three separate
// "Columns (N)" dropdown menus in the page toolbar (one per table); the toolbar now has a
// single gear icon that opens this drawer, and each table's toggles are a section in it.
// Only the sections whose table actually has rows are rendered.
interface IProps {
    body: JobsReportRequest
    data?: JobReportDataRow[]
    payments?: JobReportTechPaymentRow[]
    jobs?: Job[]
    dataColsVisibility?: DataColumnsVisibility
    dataColsVisibilitySet: (value: DataColumnsVisibility) => void
    dataStatusCols: Array<string>
    dataStatusColsSet: React.Dispatch<React.SetStateAction<Array<string>>>
    dataColsCount: number
    techColsVisibility?: TechPayColumnsVisibility
    techColsVisibilitySet: (value: TechPayColumnsVisibility) => void
    techColsCount: number
    jobsColsVisibility?: JobsColumnsVisibility
    jobsColsVisibilitySet: (value: JobsColumnsVisibility) => void
    jobsColsCount: number
    dates: any[]
    timezones: any[]
    // Committed only when Apply is pressed, as a PATCH of the fields this drawer owns.
    onApply: (patch: Partial<JobsReportRequest>) => void
    // Fired while picking a custom range, so the page can keep its chart-timing flags current.
    onCustomRange: (start: any, end: any) => void
    onClose: () => void
}

// One table's column toggles, with how many of them are currently on.
const Section = (p: { title: string, count: number, children: React.ReactNode }) => (
    <div className="mb-8">
        <div className="d-flex align-items-center mb-2">
            <div className="fs-5 fw-bold">{p.title}</div>
            <div className="badge badge-light-primary ms-2">{p.count}</div>
        </div>
        {p.children}
    </div>
)

export default function JobsReportColumnsDrawer(props: IProps) {

    const drawerRef = useRef<DrawerRef>(null);

    // The report settings are a DRAFT while the drawer is open: nothing is sent to the report
    // until Apply, so changing three fields costs one refetch instead of three. The fields are
    // controlled (value=, not defaultValue=), so the draft is the single source of truth.
    const [draft, draftSet] = useState({
        dateId: props.body.dateId,
        dateType: props.body.dateType,
        timezone: props.body.timezone ?? "job",
        startDate: props.body.startDate,
        toDate: props.body.toDate
    })

    // #chart-date only exists while this drawer is open and the range is custom, so the
    // jQuery picker is attached here. Its result lands in the draft like any other field.
    useEffect(() => {

        if (draft.dateId != "99") return;

        window.daterangecustom("chart-date", (start, end, label) => {
            var s = start.format("DD/MM/YYYY");
            var e = end.format("DD/MM/YYYY");
            draftSet(d => ({ ...d, startDate: s, toDate: e }));
            props.onCustomRange(start, end);
        });

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [draft.dateId])

    const apply = () => {
        props.onApply({
            dateId: draft.dateId,
            dateType: draft.dateType,
            timezone: draft.timezone,
            startDate: draft.startDate,
            toDate: draft.toDate
        });
        // Close by script and let the Drawer unmount itself in onClose, so KTDrawer removes
        // its backdrop.
        drawerRef.current?.close();
    }

    const {
        body, data, payments, jobs,
        dataColsVisibility, dataColsVisibilitySet, dataStatusCols, dataStatusColsSet,
        techColsVisibility, techColsVisibilitySet,
        jobsColsVisibility, jobsColsVisibilitySet
    } = props;

    return (
        <Drawer ref={drawerRef} id="drawer-jobs-report-columns"
            title="Report Settings" subtitle="Date range, what the dates mean, and the columns"
            size="normal" onClose={props.onClose}>

            <div className="p-2">

                <div className="mb-8">

                    <Select id="sel-report-dateid" name="reportDateId"
                        source={props.dates}
                        label="Date Range" labelUp block deselect={false} marginBottom={5}
                        value={draft.dateId}
                        onSelect={val => {
                            if (val) draftSet(d => ({ ...d, dateId: val }))
                        }} />

                    {
                        draft.dateId == "99" &&
                        <div className="mb-5 mt-n3">
                            {/* Kept as this exact markup because window.daterangecustom()
                                attaches the jQuery range picker to #chart-date by id. */}
                            <div className="input-group" id="chart-date">
                                <input type="text" className="form-control form-control-sm"
                                    value={(draft.startDate ?? "") + " - " + (draft.toDate ?? "")} readOnly />
                                <span className="input-group-text">
                                    <i className="fas fa-calendar fs-4"></i>
                                </span>
                            </div>
                        </div>
                    }

                    {/* Only the two real bases are offered. A report the user has never
                        touched shows its own default already selected (Dispatcher by creation
                        date, the rest by last status update) -- the earlier "Automatic" entry
                        said the same thing in words and only confused people. */}
                    <Select id="sel-report-datebasis" name="reportDateBasis"
                        source={REPORT_DATE_TYPES}
                        label="Dates Based On" labelUp block deselect={false} marginBottom={5}
                        value={draft.dateType ?? effectiveDateType({ ...body, dateType: undefined })}
                        onSelect={val => {
                            if (val) draftSet(d => ({ ...d, dateType: val }))
                        }} />

                    <div className="fs-8 text-gray-500 mt-n3 mb-5">
                        {
                            (draft.dateType ?? effectiveDateType({ ...body, dateType: undefined })) == "2"
                                ? "A job falls in the range by the date it was created."
                                : "A job falls in the range by the date its status last changed."
                        }
                    </div>

                    <Select id="sel-report-timezone" name="reportTimezone"
                        source={props.timezones}
                        label="Timezone" labelUp block deselect={false} marginBottom={5}
                        value={draft.timezone}
                        onSelect={val => {
                            if (val) draftSet(d => ({ ...d, timezone: val }))
                        }} />

                    <div className="fs-8 text-gray-500 mt-n3">
                        {
                            draft.timezone == "job"
                                ? "Each job is counted by the clock at its own address."
                                : "Every job is counted by this timezone's clock, wherever it is."
                        }
                    </div>
                </div>

                {
                    data && data.length > 0 &&
                    <Section title="Data Breakdown" count={props.dataColsCount}>

                    <div className="menu-item p-3 border-top">
                        <Checkbox label="Total Jobs" checked={dataColsVisibility?.jobs} onChange={e => dataColsVisibilitySet({
                            ...dataColsVisibility,
                            jobs: e
                        })} />
                    </div>

                    {
                        data[0].statusCounts.map((st, k) =>
                            <div key={k} className="menu-item p-3 border-top">
                                <Checkbox label={st.name} checked={dataStatusCols.includes(st.id!)}
                                    onChange={e => {
                                        var v = dataStatusCols ?? []
                                        if (e && !v.includes(st.id!)) v.push(st.id!)
                                        else v.splice(v.indexOf(st.id!), 1)
                                        dataStatusColsSet(d => [...v])
                                    }} />
                            </div>
                        )
                    }

                    <div className="menu-item p-3 border-top">
                        <Checkbox label="Conv Rate" checked={dataColsVisibility?.convrate} onChange={e => dataColsVisibilitySet({
                            ...dataColsVisibility,
                            convrate: e
                        })} />
                    </div>

                    <div className="menu-item p-3 border-top">
                        <Checkbox label="Avg Tickets" checked={dataColsVisibility?.avgtick} onChange={e => dataColsVisibilitySet({
                            ...dataColsVisibility,
                            avgtick: e
                        })} />
                    </div>

                    <div className="menu-item p-3 border-top">
                        <Checkbox label="Total Gross" checked={dataColsVisibility?.gross} onChange={e => dataColsVisibilitySet({
                            ...dataColsVisibility,
                            gross: e
                        })} />
                    </div>

                    {
                        body.segment == JobsChartSegments.JobSource &&
                        <div className="menu-item p-3 border-top">
                            <Checkbox label="Source Profit" checked={dataColsVisibility?.sourceprofit} onChange={e => dataColsVisibilitySet({
                                ...dataColsVisibility,
                                sourceprofit: e
                            })} />
                        </div>
                    }

                    <div className="menu-item p-3 border-top">
                        <Checkbox label="Total Profit" checked={dataColsVisibility?.profit} onChange={e => dataColsVisibilitySet({
                            ...dataColsVisibility,
                            profit: e
                        })} />
                    </div>
                    </Section>
                }

                {
                    payments && payments.length > 0 &&
                    <Section title="Technician Payments" count={props.techColsCount}>

                    <div className="menu-item p-3 border-top">
                        <Checkbox label="Phone" checked={techColsVisibility?.phone} onChange={e => techColsVisibilitySet({
                            ...techColsVisibility,
                            phone: e
                        })} />
                    </div>

                    <div className="menu-item p-3 border-top">
                        <Checkbox label="Metros" checked={techColsVisibility?.metro} onChange={e => techColsVisibilitySet({
                            ...techColsVisibility,
                            metro: e
                        })} />
                    </div>

                    <div className="menu-item p-3 border-top">
                        <Checkbox label="Types" checked={techColsVisibility?.type} onChange={e => techColsVisibilitySet({
                            ...techColsVisibility,
                            type: e
                        })} />
                    </div>

                    <div className="menu-item p-3 border-top">
                        <Checkbox label="Sources" checked={techColsVisibility?.source} onChange={e => techColsVisibilitySet({
                            ...techColsVisibility,
                            source: e
                        })} />
                    </div>

                    <div className="menu-item p-3 border-top">
                        <Checkbox label="Closed Jobs" checked={techColsVisibility?.closedJobs} onChange={e => techColsVisibilitySet({
                            ...techColsVisibility,
                            closedJobs: e
                        })} />
                    </div>

                    <div className="menu-item p-3 border-top">
                        <Checkbox label="Total" checked={techColsVisibility?.total} onChange={e => techColsVisibilitySet({
                            ...techColsVisibility,
                            total: e
                        })} />
                    </div>

                    <div className="menu-item p-3 border-top">
                        <Checkbox label="Cash" checked={techColsVisibility?.cash} onChange={e => techColsVisibilitySet({
                            ...techColsVisibility,
                            cash: e
                        })} />
                    </div>

                    <div className="menu-item p-3 border-top">
                        <Checkbox label="CC Charge" checked={techColsVisibility?.cccharge} onChange={e => techColsVisibilitySet({
                            ...techColsVisibility,
                            cccharge: e
                        })} />
                    </div>

                    <div className="menu-item p-3 border-top">
                        <Checkbox label="CC Manual" checked={techColsVisibility?.ccmanual} onChange={e => techColsVisibilitySet({
                            ...techColsVisibility,
                            ccmanual: e
                        })} />
                    </div>

                    <div className="menu-item p-3 border-top">
                        <Checkbox label="Check" checked={techColsVisibility?.check} onChange={e => techColsVisibilitySet({
                            ...techColsVisibility,
                            check: e
                        })} />
                    </div>

                    <div className="menu-item p-3 border-top">
                        <Checkbox label="E-Pay" checked={techColsVisibility?.epay} onChange={e => techColsVisibilitySet({
                            ...techColsVisibility,
                            epay: e
                        })} />
                    </div>

                    <div className="menu-item p-3 border-top">
                        <Checkbox label="Rinvox Pay" checked={techColsVisibility?.rinvoxPay} onChange={e => techColsVisibilitySet({
                            ...techColsVisibility,
                            rinvoxPay: e
                        })} />
                    </div>

                    <div className="menu-item p-3 border-top">
                        <Checkbox label="Balance" checked={techColsVisibility?.techBalance} onChange={e => techColsVisibilitySet({
                            ...techColsVisibility,
                            techBalance: e
                        })} />
                    </div>
                    </Section>
                }

                {
                    jobs && jobs.length > 0 &&
                    <Section title="Jobs" count={props.jobsColsCount}>
                    <div className="menu-item p-3 border-top">
                        <Checkbox label="Status" checked={jobsColsVisibility?.status} onChange={e => jobsColsVisibilitySet({
                            ...jobsColsVisibility,
                            status: e
                        })} />
                    </div>
                    <div className="menu-item p-3 border-top">
                        <Checkbox label="Name" checked={jobsColsVisibility?.name} onChange={e => jobsColsVisibilitySet({
                            ...jobsColsVisibility,
                            name: e
                        })} />
                    </div>
                    <div className="menu-item p-3 border-top">
                        <Checkbox label="Phone" checked={jobsColsVisibility?.phone} onChange={e => jobsColsVisibilitySet({
                            ...jobsColsVisibility,
                            phone: e
                        })} />
                    </div>
                    <div className="menu-item p-3 border-top">
                        <Checkbox label="Job Type" checked={jobsColsVisibility?.type} onChange={e => jobsColsVisibilitySet({
                            ...jobsColsVisibility,
                            type: e
                        })} />
                    </div>
                    <div className="menu-item p-3 border-top">
                        <Checkbox label="Job Source" checked={jobsColsVisibility?.source} onChange={e => jobsColsVisibilitySet({
                            ...jobsColsVisibility,
                            source: e
                        })} />
                    </div>
                    <div className="menu-item p-3 border-top">
                        <Checkbox label="Address" checked={jobsColsVisibility?.address} onChange={e => jobsColsVisibilitySet({
                            ...jobsColsVisibility,
                            address: e
                        })} />
                    </div>
                    <div className="menu-item p-3 border-top">
                        <Checkbox label="Metro" checked={jobsColsVisibility?.metro} onChange={e => jobsColsVisibilitySet({
                            ...jobsColsVisibility,
                            metro: e
                        })} />
                    </div>
                    <div className="menu-item p-3 border-top">
                        <Checkbox label="Tech" checked={jobsColsVisibility?.tech} onChange={e => jobsColsVisibilitySet({
                            ...jobsColsVisibility,
                            tech: e
                        })} />
                    </div>
                    <div className="menu-item p-3 border-top">
                        <Checkbox label="Created At" checked={jobsColsVisibility?.createdDate} onChange={e => jobsColsVisibilitySet({
                            ...jobsColsVisibility,
                            createdDate: e
                        })} />
                    </div>
                    <div className="menu-item p-3 border-top">
                        <Checkbox label={body.segment == JobsChartSegments.CanceledJobs ? "Canceled At" : body.segment == JobsChartSegments.ClosedJobs ? "Closed At" : "Closed At"} checked={jobsColsVisibility?.lastUpdate} onChange={e => jobsColsVisibilitySet({
                            ...jobsColsVisibility,
                            lastUpdate: e
                        })} />
                    </div>
                    <div className="menu-item p-3 border-top">
                        <Checkbox label="Appt Date" checked={jobsColsVisibility?.apptDate} onChange={e => jobsColsVisibilitySet({
                            ...jobsColsVisibility,
                            apptDate: e
                        })} />
                    </div>
                    {
                        body.segment == JobsChartSegments.CanceledJobs &&
                        <div className="menu-item p-3 border-top">
                            <Checkbox label="Note" checked={jobsColsVisibility?.cancelNote} onChange={e => jobsColsVisibilitySet({
                                ...jobsColsVisibility,
                                cancelNote: e
                            })} />
                        </div>
                    }
                    {
                        body.segment != JobsChartSegments.CanceledJobs &&
                        <>
                            <div className="menu-item p-3 border-top">
                                <Checkbox label="Inv Number" checked={jobsColsVisibility?.invNo} onChange={e => jobsColsVisibilitySet({
                                    ...jobsColsVisibility,
                                    invNo: e
                                })} />
                            </div>
                            <div className="menu-item p-3 border-top">
                                <Checkbox label="Total" checked={jobsColsVisibility?.total} onChange={e => jobsColsVisibilitySet({
                                    ...jobsColsVisibility,
                                    total: e
                                })} />
                            </div>
                            <div className="menu-item p-3 border-top">
                                <Checkbox label="Cash" checked={jobsColsVisibility?.cash} onChange={e => jobsColsVisibilitySet({
                                    ...jobsColsVisibility,
                                    cash: e
                                })} />
                            </div>
                            <div className="menu-item p-3 border-top">
                                <Checkbox label="CC Manual" checked={jobsColsVisibility?.ccmanual} onChange={e => jobsColsVisibilitySet({
                                    ...jobsColsVisibility,
                                    ccmanual: e
                                })} />
                            </div>
                            <div className="menu-item p-3 border-top">
                                <Checkbox label="CC Charge" checked={jobsColsVisibility?.cccharge} onChange={e => jobsColsVisibilitySet({
                                    ...jobsColsVisibility,
                                    cccharge: e
                                })} />
                            </div>
                            <div className="menu-item p-3 border-top">
                                <Checkbox label="Check" checked={jobsColsVisibility?.check} onChange={e => jobsColsVisibilitySet({
                                    ...jobsColsVisibility,
                                    check: e
                                })} />
                            </div>
                            <div className="menu-item p-3 border-top">
                                <Checkbox label="E-Pay" checked={jobsColsVisibility?.epay} onChange={e => jobsColsVisibilitySet({
                                    ...jobsColsVisibility,
                                    epay: e
                                })} />
                            </div>
                            <div className="menu-item p-3 border-top">
                                <Checkbox label="Rinvox Pay" checked={jobsColsVisibility?.rinvoxPay} onChange={e => jobsColsVisibilitySet({
                                    ...jobsColsVisibility,
                                    rinvoxPay: e
                                })} />
                            </div>
                            <div className="menu-item p-3 border-top">
                                <Checkbox label="Tech Parts" checked={jobsColsVisibility?.techParts} onChange={e => jobsColsVisibilitySet({
                                    ...jobsColsVisibility,
                                    techParts: e
                                })} />
                            </div>
                            <div className="menu-item p-3 border-top">
                                <Checkbox label="CO Parts" checked={jobsColsVisibility?.coParts} onChange={e => jobsColsVisibilitySet({
                                    ...jobsColsVisibility,
                                    coParts: e
                                })} />
                            </div>

                            <div className="menu-item p-3 border-top">
                                <Checkbox label="Tax" checked={jobsColsVisibility?.tax} onChange={e => jobsColsVisibilitySet({
                                    ...jobsColsVisibility,
                                    tax: e
                                })} />
                            </div>

                            <div className="menu-item p-3 border-top">
                                <Checkbox label="Tech Profit" checked={jobsColsVisibility?.techProfits} onChange={e => jobsColsVisibilitySet({
                                    ...jobsColsVisibility,
                                    techProfits: e
                                })} />
                            </div>
                            <div className="menu-item p-3 border-top">
                                <Checkbox label="Source Profit" checked={jobsColsVisibility?.sourceProfits} onChange={e => jobsColsVisibilitySet({
                                    ...jobsColsVisibility,
                                    sourceProfits: e
                                })} />
                            </div>
                            <div className="menu-item p-3 border-top">
                                <Checkbox label="CO Profit" checked={jobsColsVisibility?.coProfits} onChange={e => jobsColsVisibilitySet({
                                    ...jobsColsVisibility,
                                    coProfits: e
                                })} />
                            </div>

                            <div className="menu-item p-3 border-top">
                                <Checkbox label="Tech Fee" checked={jobsColsVisibility?.techFee} onChange={e => jobsColsVisibilitySet({
                                    ...jobsColsVisibility,
                                    techFee: e
                                })} />
                            </div>

                            <div className="menu-item p-3 border-top">
                                <Checkbox label="Tech Balance" checked={jobsColsVisibility?.techBalance} onChange={e => jobsColsVisibilitySet({
                                    ...jobsColsVisibility,
                                    techBalance: e
                                })} />
                            </div>
                        </>
                    }
                    </Section>
                }

                {/* Pinned to the drawer's bottom edge so Apply is reachable without scrolling
                    past every column toggle. */}
                <div className="position-sticky bottom-0 bg-body border-top pt-4 pb-2">
                    <button type="button" className="btn btn-primary w-100" onClick={apply}>
                        Apply
                    </button>
                </div>

            </div>

        </Drawer>
    )
}
